import useUserContext from 'Context/UserContext';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import React, { useMemo, useState } from 'react';
import fr from 'facet-rest';
import useActionItemSecureFiles from 'Hooks/useActionItemSecureFiles';
import { TYPES } from 'Consts/actionItem';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { navigateToPage } from 'Views/Page/Content/Templates';
import { goToAccountOpeningFlow, goToAccountRetitlingFlow } from 'Helpers/Navigation';
import classNames from 'classnames';
import { Paper, Tooltip } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';
import Typography from 'Components/UI/Typography';
import TimerIcon from '@material-ui/icons/Timer';
import moment from 'moment';
import CheckIcon from '@material-ui/icons/Check';
import TaskUploader from 'Components/ComponentLibrary/Components/Uncategorized/TaskUploader';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import PropTypes from 'prop-types';
import { navigateToFunding } from 'Helpers/AccountFunding';
import useMobileLayout from 'Hooks/useMobileLayout';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { getStartOfDayLocalFromUTC } from 'Helpers/DateTime';
import { DataValueMap } from 'Helpers/Testing';
import {
  AttachmentsSectionRebranded,
} from 'Components/ComponentLibrary/Components/Uncategorized/AttachmentsSectionRebranded';

export const getPriorityConfig = (theme, priority) => {
  switch (priority) {
    case 'high':
      return {
        label: 'High',
        color: theme.palette.primary.main,
      };
    case 'medium':
      return {
        label: 'Med',
        color: theme.palette.facet.primaryBlueMuted,
      };
    case 'low':
      return {
        label: 'Low',
        color: theme.palette.facet.accentBlue14,
      };
    default:
      return {};
  }
};

const useStyles = buildUseStyles('MessagesActionItemCard', (theme) => ({
  root: {
    padding: theme.spacing(1, 0),
  },
  paper: {
    width: '100%',
    display: 'flex',
    padding: theme.spacing(2),
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  paperMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: `${theme.spacing(0.5)}px`,
    flexGrow: 0.05,
  },
  name: {
    flex: '1 0 0',
    minWidth: 0,
  },
  dueDate: {
    color: theme.palette.facet.accentBlue,
  },
  completed: {
    color: theme.palette.secondary.main,
  },
  completedStatusContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  completedStatusText: {
    marginRight: theme.spacing(1),
  },
  taskButton: {
    padding: theme.spacing(0, 1),
    borderRadius: '16px',
  },
  viewTaskButton: {
    backgroundColor: theme.palette.facet.accentBlue,
    color: 'white',
  },
  chip: {
    color: 'white',
    fontWeight: '400',
    flexShrink: 0,
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  attachFileButton: {
    margin: 0,
  },
  fileList: {
    paddingTop: theme.spacing(1),
    display: 'flex',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  actionsMobile: {
    width: '100%',
    justifyContent: 'space-between',
  },
  infoContainer: {
    display: 'flex',
    width: '100%',
  },
}));

const MessagesActionItemCard = ({
  className,
  actionItem,
}) => {
  const { isAgent } = useUserContext();
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();

  const { loadModalCollectionByLink } = useModalContext();

  const [actionItemResource, setActionItemResource] = useState(actionItem);

  const isAirTask = Boolean(actionItemResource?.relationship(fr.relationships.annualInvestmentReview.name)?.link('related'));

  const refreshActionItem = async () => {
    const [refreshedActionItem] = await actionItemResource.link('self').get();
    setActionItemResource(refreshedActionItem);
  };

  const { fileCollection, setFileCollection } = useActionItemSecureFiles(actionItemResource, {
    secureFilesApplicable: !isAirTask,
  });

  const isAccountOpeningTask = actionItemResource?.relationship(fr.relationships.accountOpeningWizard.name);
  const accountOpeningOrFundingTypes = [TYPES.ACTION_ITEM_TYPE_FUND_ACCOUNT, TYPES.ACTION_ITEM_TYPE_OPEN_ACCOUNT, TYPES.ACTION_ITEM_TYPE_OPEN_AND_FUND_ACCOUNT];
  const isAccountOpeningOrFundingTask = accountOpeningOrFundingTypes.includes(actionItemResource?.attr('actionItemType'));
  const isAccountRetitlingTask = actionItemResource?.relationship(fr.relationships.accountRetitlingWizard.name);

  const statusCompleted = actionItemResource?.attr('status') === 'completed';
  const notCompletedAndNotSpecial = !statusCompleted && !isAccountOpeningOrFundingTask && !isAccountRetitlingTask && !isAirTask;

  let showOpenModal = true;
  if (isAccountOpeningTask && statusCompleted) {
    showOpenModal = false;
  }
  if (isAirTask && statusCompleted) {
    showOpenModal = false;
  }

  const { color, label } = getPriorityConfig(theme, actionItemResource?.attr('priority'));
  const showLabel = !isMobile && label;

  const mobileViewTaskText = useMemo(() => {
    if (isAgent) return 'View';
    if (isAirTask) {
      return statusCompleted ? 'Completed' : 'Review';
    }

    switch (actionItemResource?.attr('actionItemType')) {
      case TYPES.ACTION_ITEM_TYPE_FUND_ACCOUNT: {
        return 'Fund';
      }
      case TYPES.ACTION_ITEM_TYPE_OPEN_AND_FUND_ACCOUNT || TYPES.ACTION_ITEM_TYPE_OPEN_ACCOUNT: {
        return statusCompleted ? 'Completed' : 'Open';
      }
      default: {
        return 'View';
      }
    }
  }, [actionItemResource]);

  const desktopViewTaskText = useMemo(() => {
    if (isAgent) return 'View Task';
    if (isAirTask) {
      return statusCompleted ? 'Completed' : 'Complete Review';
    }

    switch (actionItemResource?.attr('actionItemType')) {
      case TYPES.ACTION_ITEM_TYPE_FUND_ACCOUNT: {
        return 'Fund Account';
      }
      case TYPES.ACTION_ITEM_TYPE_OPEN_AND_FUND_ACCOUNT || TYPES.ACTION_ITEM_TYPE_OPEN_ACCOUNT: {
        return statusCompleted ? 'Completed' : 'Complete Opening';
      }
      default: {
        return 'View Action';
      }
    }
  }, [actionItemResource]);

  const viewTaskText = isMobile ? mobileViewTaskText : desktopViewTaskText;

  const markAsComplete = async () => {
    try {
      actionItemResource.attr('status', 'completed');
      await actionItemResource.submit();
      await refreshActionItem();
      displaySuccess(`${actionItemResource.attr('name')} has been completed!`);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const openActionItemDefaultAction = () => {
    loadModalCollectionByLink(actionItemResource.link('self').url());
  };

  const openActionItemModal = async () => {
    if (isAgent) {
      return openActionItemDefaultAction();
    }
    if (isAirTask) {
      const [annualInvestmentReviewStep] = await actionItemResource?.relationship(fr.relationships.annualInvestmentReview.name)?.link('related').get();
      navigateToPage(annualInvestmentReviewStep.link('self'));
      return null;
    }

    switch (actionItemResource?.attr('actionItemType')) {
      case TYPES.ACTION_ITEM_TYPE_FUND_ACCOUNT: {
        navigateToFunding(actionItemResource);
        return null;
      }
      case TYPES.ACTION_ITEM_TYPE_OPEN_ACCOUNT || TYPES.ACTION_ITEM_TYPE_OPEN_AND_FUND_ACCOUNT: {
        await goToAccountOpeningFlow(actionItemResource);
        return null;
      }
      case TYPES.ACTION_ITEM_TYPE_RETITLE_ACCOUNT: {
        await goToAccountRetitlingFlow(actionItemResource);
        return null;
      }
      default: {
        return openActionItemDefaultAction();
      }
    }
  };


  return (
    <div className={classNames(classes.root, className)}>
      <Paper
        className={classNames(classes.paper, { [classes.paperMobile]: isMobile })}
        variant="tight"
        {...DataValueMap.init('message-action-item-card').build()}
      >
        {showLabel && <Chip className={classes.chip} label={label} style={{ backgroundColor: color }} />}
        <Box className={classes.infoContainer}>
          <Tooltip title={actionItemResource?.attr('name')}>
            <Typography
              test={'task-message'}
              variant="h5"
              color="primaryGray"
              weight="medium"
              className={classes.name}
            >
              {actionItemResource?.attr('name')}
            </Typography>
          </Tooltip>
          <div
            className={classNames(classes.status, {
              [classes.dueDate]: !statusCompleted,
              [classes.completed]: statusCompleted,
            })}
          >
            {!statusCompleted ? (
              <React.Fragment>
                <TimerIcon />
                <Typography>
                  {moment(getStartOfDayLocalFromUTC(actionItemResource?.attr('dueDate'))).format('M/DD')}
                </Typography>
              </React.Fragment>
            ) : (
              <div
                test={`action-item-${actionItemResource?.id()}-marked-completed-label`}
                className={classes.completedStatusContainer}
              >
                <Typography className={classes.completedStatusText}>Completed</Typography>
                <CheckIcon />
              </div>
            )}
          </div>
        </Box>
        <Box className={classes.actions}>
          {notCompletedAndNotSpecial && (
          <TaskUploader
            className={classNames(classes.noWrap, classes.attachFileButton)}
            actionItemResource={actionItemResource}
            fileCollection={fileCollection}
            setFileCollection={setFileCollection}
            onMarkComplete={refreshActionItem}
          />
          )}
          {showOpenModal && (
          <Button
            className={classNames(classes.taskButton, classes.viewTaskButton, classes.noWrap)}
            onClick={() => openActionItemModal()}
            variant="contained"
            color="primary"
            test={'view-task'}
            size="xs"
          >
            {viewTaskText}
          </Button>
          )}
          {notCompletedAndNotSpecial && (
          <Button
            className={classNames(classes.taskButton, classes.markCompleteButton, classes.noWrap)}
            onClick={markAsComplete}
            variant="contained"
            color="primary"
            test={`action-item-${actionItemResource?.id()}-mark-complete`}
            size="xs"
          >
            {isMobile ? 'Complete' : 'Mark Complete'}
          </Button>
          )}
        </Box>
      </Paper>
      <div className={classes.fileList}>
        {fileCollection && <AttachmentsSectionRebranded attachmentsCollection={fileCollection} eventCategory={'Messages'} />}
      </div>
    </div>
  );
};

MessagesActionItemCard.defaultProps = {
  className: '',
};

MessagesActionItemCard.propTypes = {
  className: PropTypes.string,
  actionItem: PropTypes.object.isRequired,
};


export default MessagesActionItemCard;
